<template>
    <div class="flex flex-col justify-start items-start gap-1.5 grow">
        <label
            v-if="label"
            class="text-sm font-medium label grow-0 text-gray-700"
        >
            {{ label }}
        </label>
        <input
            v-bind="$attrs"
            ref="input"
            v-model="inputValue"
            class="peer"
            @input="debouncedValidateValue()"
        />
        <span
            v-if="$attrs.maxlength"
            class="text-xs font-normal -mt-1 ml-auto text-gray-400 hidden peer-focus:block"
        >
            {{ nCharactersLeft }} characters left
        </span>
        <span
            v-if="validationMessage !== null"
            class="text-xs font-normal text-error-500 hidden peer-invalid:block"
        >
            {{ validationMessage }}
        </span>
    </div>
</template>

<script lang="ts">
export default {
    name: "EaiInput",
    inheritAttrs: false,
}
</script>

<script setup lang="ts">
import { ref, computed, useAttrs } from "vue"
import { debounce } from "ts-debounce"

// returns a string with the error message; empty string if input is valid.
export type InputValidator = (inputValue: string) => string

export interface Props {
    modelValue: string | null | number
    label?: string | null
    validator?: InputValidator | null
}
const props = withDefaults(defineProps<Props>(), {
    label: null,
    validator: null,
})

const emit = defineEmits<{
    (e: "update:model-value", value: string): void
}>()
const inputValue = computed({
    get: () => props.modelValue,
    set: (value: string) => emit("update:model-value", value),
})

const input = ref<HTMLInputElement | null>(null)
/**
 * Helper method to set the HTML5 validation error message. Note that this can
 * be used by a parent component to set the error message when performing async
 * server-side validtaion.
 *
 * @param errorMessage the error message. Empty string means field is valid.
 * @param reportValidity if true displays popup with error.
 */
const setErrorMessage = (errorMessage: string, reportValidity = false) => {
    if (!input.value) throw "Input not initialised"
    input.value.setCustomValidity(errorMessage)
    if (reportValidity) input.value.reportValidity()
    validationMessage.value = errorMessage
}

const attrs = useAttrs()
const nCharactersLeft = computed(() => {
    if (attrs.maxlength)
        try {
            return (
                parseInt(attrs.maxlength) -
                (props.modelValue?.toString().length ?? 0)
            )
        } catch {
            return null
        }
    else return null
})

defineExpose({ setErrorMessage })

const DEBOUNCE_TIMEOUT = 400 // msec
// This is needed because input.validationMessage is not reactive
const validationMessage = ref<string | null>(null)
const validateValue = () => {
    input.value?.setCustomValidity("")
    if (props.validator)
        setErrorMessage(props.validator(props.modelValue?.toString() || ""))
    validationMessage.value = input.value?.validationMessage ?? null
}
/**
 * Validate the input on user typing. Note that it's debounced so that the error
 * message is displayed only when the user stops typing.
 */
const debouncedValidateValue = debounce(validateValue, DEBOUNCE_TIMEOUT)
</script>

<style lang="css">
@import "./input.css";
</style>
